import React from "react";
import { ICommandBarItemProps, IContextualMenuItem } from "office-ui-fabric-react";
import SelectionModeSwitchableCommandBar from "../../common/SelectionModeSwitchableCommandBar";
import ListMenuFilterSelector from "../../common/ListMenuFilterSelector";
import { BulkUpdatePeriodsModel, TimeReportingPeriodStatus } from "../../../store/TimeReportingPeriodsStore";
import * as Metadata from "../../../entities/Metadata";
import { notUndefined } from "../../utils/common";

interface Props {
    selectedCount: number;
    onBulkCreate: () => void;
    onUpdate: (update: BulkUpdatePeriodsModel) => void;
    onDelete: () => void;
    onCancelSelection: () => void;
    timesheetApprovalsEnabled: boolean;
    filters: Metadata.IFilter<Metadata.BaseFilterValue>[];
    activeFilter?: Metadata.IFilter<Metadata.BaseFilterValue>;
    onFilterChange: (filterId: string) => void;
    canManageConfiguration: boolean;
    canManage: boolean;
}

const TimeReportingPeriodsCommandBar = (props: Props) => {
    const {
        selectedCount,
        onBulkCreate,
        onUpdate,
        onDelete,
        onCancelSelection,
        timesheetApprovalsEnabled,
        filters,
        activeFilter,
        onFilterChange,
        canManageConfiguration,
        canManage
    } = props;

    const normalModeItems: ICommandBarItemProps[] = [
        {
            key: "bulk-create",
            text: "Bulk Create",
            iconProps: { iconName: "Add" },
            onClick: onBulkCreate,
            disabled: !canManage
        }
    ];

    const buildFarCommands = (): IContextualMenuItem[] => {
        return [
            {
                key: "filter",
                onRender: () => (
                    <ListMenuFilterSelector
                        filters={filters}
                        activeFilter={activeFilter}
                        canManageConfiguration={canManageConfiguration}
                        onActiveItemChanged={id => {
                            const newActiveFilter = filters.find(_ => _.id === id);
                            if (newActiveFilter) {
                                onFilterChange(id);
                            }
                        }}
                    />
                )
            }
        ];
    };

    const buildSelectionModeItems = (): ICommandBarItemProps[] => {
        if (!canManage) {
            return [];
        }

        return [
            {
                key: "set-status",
                text: "Set Status",
                iconProps: { iconName: "CircleRing" },
                subMenuProps: {
                    items: [
                        {
                            key: "status-open",
                            text: "Open",
                            onClick: () => onUpdate({ status: TimeReportingPeriodStatus.Open })
                        },
                        {
                            key: "status-closed",
                            text: "Closed",
                            onClick: () => onUpdate({ status: TimeReportingPeriodStatus.Closed })
                        }
                    ]
                }
            },
            timesheetApprovalsEnabled
                ? {
                      key: "set-approval-required",
                      text: "Set Approval Required",
                      iconProps: { iconName: "DocumentApproval" },
                      subMenuProps: {
                          items: [
                              {
                                  key: "approval-yes",
                                  text: "Yes",
                                  onClick: () => onUpdate({ approvalRequired: true })
                              },
                              {
                                  key: "approval-no",
                                  text: "No",
                                  onClick: () => onUpdate({ approvalRequired: false })
                              }
                          ]
                      }
                  }
                : undefined,
            {
                key: "delete",
                text: "Delete",
                iconProps: { iconName: "Delete" },
                onClick: onDelete
            }
        ].filter(notUndefined);
    };

    const selectionModeItems = buildSelectionModeItems();

    return (
        <SelectionModeSwitchableCommandBar
            items={normalModeItems}
            farItems={buildFarCommands()}
            selectionMode={{
                enabled: selectedCount > 0,
                items: selectionModeItems,
                onCancel: onCancelSelection,
                selectedCount: selectedCount
            }}
            entityTypeLabels={{ singular: "period", plural: "periods" }}
        />
    );
};

export default TimeReportingPeriodsCommandBar;
